import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY


export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database table names
export const TABLES = {
  LEADS: 'leads',
  ORDERS: 'orders',
  TAGS: 'tags'
}

// Helper functions for common operations
export const dbHelpers = {
  // Lead operations
  async getLeads(userId) {
    // Query for leads assigned to the user or created by the user
    let { data, error } = await supabase
      .from(TABLES.LEADS)
      .select('*')
      .or(`assigned_to.eq.${userId},user_id.eq.${userId}`)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  },

  async createLead(leadData) {
    // Prepare lead data, handling optional columns gracefully
    const cleanLeadData = { ...leadData };
    
    // Ensure email is always provided (even if empty string)
    if (!cleanLeadData.email) {
      cleanLeadData.email = '';
    }
    
    // Handle tags array - only include if it has values
    if (cleanLeadData.tags && Array.isArray(cleanLeadData.tags) && cleanLeadData.tags.length === 0) {
      delete cleanLeadData.tags;
    }
    
    // Handle new form fields - only include if they have values
    const newFields = ['city', 'state', 'age', 'action', 'nextAction', 'followUpDate'];
    newFields.forEach(field => {
      if (cleanLeadData[field] === '' || cleanLeadData[field] === null || cleanLeadData[field] === undefined) {
        delete cleanLeadData[field];
      }
    });
    
    // Convert nextAction to next_action for database
    if (cleanLeadData.nextAction) {
      cleanLeadData.next_action = cleanLeadData.nextAction;
      delete cleanLeadData.nextAction;
    }
    
    // Convert followUpDate to follow_up_date for database
    if (cleanLeadData.followUpDate) {
      cleanLeadData.follow_up_date = cleanLeadData.followUpDate;
      delete cleanLeadData.followUpDate;
    }
    
    // Remove null values but keep empty strings for required fields
    Object.keys(cleanLeadData).forEach(key => {
      if (cleanLeadData[key] === null) {
        delete cleanLeadData[key];
      }
    });
    
    const { data, error } = await supabase
      .from(TABLES.LEADS)
      .insert([cleanLeadData])
      .select()
    
    if (error) throw error
    return data[0]
  },

  async updateLead(id, updates) {
    // Handle new form fields - only include if they have values
    const cleanUpdates = { ...updates };
    
    // Handle new form fields - only include if they have values
    const newFields = ['city', 'state', 'age', 'action', 'nextAction', 'followUpDate'];
    newFields.forEach(field => {
      if (cleanUpdates[field] === '' || cleanUpdates[field] === null || cleanUpdates[field] === undefined) {
        delete cleanUpdates[field];
      }
    });
    
    // Convert nextAction to next_action for database
    if (cleanUpdates.nextAction) {
      cleanUpdates.next_action = cleanUpdates.nextAction;
      delete cleanUpdates.nextAction;
    }
    
    // Convert followUpDate to follow_up_date for database
    if (cleanUpdates.followUpDate) {
      cleanUpdates.follow_up_date = cleanUpdates.followUpDate;
      delete cleanUpdates.followUpDate;
    }
    
    // Remove null values
    Object.keys(cleanUpdates).forEach(key => {
      if (cleanUpdates[key] === null) {
        delete cleanUpdates[key];
      }
    });
    
    const { data, error } = await supabase
      .from(TABLES.LEADS)
      .update(cleanUpdates)
      .eq('id', id)
      .select()
    
    if (error) throw error
    return data[0]
  },

  async deleteLead(id) {
    const { error } = await supabase
      .from(TABLES.LEADS)
      .delete()
      .eq('id', id)
    
    if (error) throw error
  },

  // Order operations
  async getOrders(userId) {
    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .select(`
        *,
        leads (
          name,
          phone
        )
      `)
      .eq('user_id', userId)
      .order('conversion_date', { ascending: false })

    if (error) throw error
    return data || []
  },

  async createOrder(orderData) {
    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .insert([orderData])
      .select()
    
    if (error) throw error
    return data[0]
  },

  async updateOrder(id, updates) {
    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .update(updates)
      .eq('id', id)
      .select()
    
    if (error) throw error
    return data[0]
  },





  // Tag operations
  async getTags(userId) {
    const { data, error } = await supabase
      .from(TABLES.TAGS)
      .select('*')
      .eq('user_id', userId)
      .order('name')
    
    if (error) throw error
    return data
  },

  async createTag(tagData) {
    const { data, error } = await supabase
      .from(TABLES.TAGS)
      .insert([tagData])
      .select()
    
    if (error) throw error
    return data[0]
  }
}
